import React, { useMemo } from 'react'
import { styled } from '@mui/system'
import Divider from '@mui/material/Divider'
import { DialogContent } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import FormTextField from 'components/atoms/FormTextField'

import useEntityData from 'hooks/useEntityData'

import { ROLE_ADMIN, ROLE_PARTNER_ADMIN, ROLE_SUPER_ADMIN } from '../../../auth'

const createStatuses = (t) => [
  { label: t('users.status.disabled'), value: 'disabled' },
  { label: t('users.status.enabled'), value: 'enabled' },
]
const createLanguages = (t) => [
  { label: t('users.languages.it'), value: 'it' },
  { label: t('users.languages.en'), value: 'en' },
  { label: t('users.languages.de'), value: 'de' },
  { label: t('users.languages.fr'), value: 'fr' },
]
const createReceiveEmailDisabled = (t) => [
  { label: t('users.receiveEmailDisabled.enabled'), value: false },
  { label: t('users.receiveEmailDisabled.disabled'), value: true },
]
const createAccountTypes = (t) => [
  { label: t(`users.accountTypes.${ROLE_ADMIN}`), value: ROLE_ADMIN },
  {
    label: t(`users.accountTypes.${ROLE_PARTNER_ADMIN}`),
    value: ROLE_PARTNER_ADMIN,
  },
  {
    label: t(`users.accountTypes.${ROLE_SUPER_ADMIN}`),
    value: ROLE_SUPER_ADMIN,
  },
]

const UserInfoFields = ({ allowedRoles }) => {
  const { values } = useFormikContext()

  const { items } = useEntityData({
    entity: 'partners',
    newPage: 0,
    limit: 9999,
  })
  const { t } = useTranslation()

  const statuses = useMemo(() => createStatuses(t), [t])
  const languages = useMemo(() => createLanguages(t), [t])
  const receiveEmailDisabled = useMemo(() => createReceiveEmailDisabled(t), [t])
  const accountTypes = useMemo(() => createAccountTypes(t), [t])

  const showPartner = useMemo(
    () => allowedRoles.includes(ROLE_SUPER_ADMIN),
    [allowedRoles]
  )

  return (
    <>
      <DialogContent>
        <Row>
          <FormTextField
            name="firstName"
            label={t('users.userInfoDialog.name')}
            fullWidth
          />
          <FormTextField
            name="lastName"
            label={t('users.userInfoDialog.lastName')}
            fullWidth
          />
        </Row>

        <FormTextField
          name="email"
          label={t('users.userInfoDialog.email')}
          fullWidth
        />
        <Row>
          <FormTextField
            select
            name="language"
            label={t('users.userInfoDialog.language')}
            fullWidth
          >
            {languages.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </FormTextField>
          <FormTextField
            select
            name="receiveEmailDisabled"
            label={t('users.userInfoDialog.receiveEmailDisabled')}
            fullWidth
          >
            {
              // eslint-disable-next-line sonarjs/no-identical-functions
              receiveEmailDisabled.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))
            }
          </FormTextField>
        </Row>
      </DialogContent>

      <Divider />

      <DialogContent>
        <Row>
          <FormTextField
            select
            name="accountType"
            label={t('users.userInfoDialog.accountType')}
            fullWidth
          >
            {accountTypes
              .filter((item) => allowedRoles.includes(item.value))
              // eslint-disable-next-line sonarjs/no-identical-functions
              .map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </FormTextField>
          {showPartner && values?.accountType === 'partnerAdmin' && (
            <FormTextField
              select
              name="partnerId"
              label={t('users.userInfoDialog.partner')}
              fullWidth
            >
              {items.map((option) => (
                <MenuItem key={option.partnerId} value={option.partnerId}>
                  {option.name}
                </MenuItem>
              ))}
            </FormTextField>
          )}
          <FormTextField
            select
            name="status"
            label={t('users.userInfoDialog.status')}
            fullWidth
          >
            {
              // eslint-disable-next-line sonarjs/no-identical-functions
              statuses.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))
            }
          </FormTextField>
        </Row>
      </DialogContent>

      <Divider />
    </>
  )
}

export default UserInfoFields

const Row = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '& > div:first-of-type': {
    marginRight: theme.spacing(2),
  },
  '& > div:nth-of-type(3)': {
    marginLeft: theme.spacing(2),
  },
}))
